import { prepareReimburseTree } from "@/utils/reimburseCategory";
import { FC, useMemo, useContext, useEffect, useState } from "react";
import { Modal, Form, Button, InputNumber, Input, DatePicker, notification, Flex, Spin, Tabs } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { ReimbursRequest, AddReimburseRequestItem, ReimburseCategory, AddReimburseRequest } from "@/types/reimburse";
import { useTranslation } from "react-i18next";
import { ReimburseTreeSelect } from "@/components/Selector";
import { addReimburse, applicationSubmit, treeList } from "@/apis/reimburse";
import dayjs from "dayjs";
import { SConModal } from "@/styles/SConModal";
import SDoc from "@/components/SDoc";
import { DictContext } from "@/layout/context/DictContext";

interface AddReimbursementProps {
  open: boolean;
  onSuccess: (data: ReimbursRequest) => void;
  onClose: () => void;
}

interface ItemTabState {
  [key: number]: string;
}

const AddReimbursement: FC<AddReimbursementProps> = ({
  open,
  onSuccess,
  onClose,
}) => {
  const { t } = useTranslation();
  const { exchangeRate } = useContext(DictContext);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState<"ADD" | "SUBMIT" | undefined>(undefined);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeData, setTreeData] = useState<ReimburseCategory[]>([]);
  const preparedTree = useMemo(() => prepareReimburseTree(treeData), [treeData]);
  const [itemActiveTabs, setItemActiveTabs] = useState<ItemTabState>({});

  useEffect(() => {
    fetchTreeData();
  }, [])

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldValue("items", [{ expenseDate: dayjs() }]);
      setItemActiveTabs({});
    }
  }, [open, form]);

  const fetchTreeData = () => {
    setTreeLoading(true);
    treeList()
      .then((res) => {
        setTreeData(res);
      })
      .catch(() => {
        setTreeData([]);
      })
      .finally(() => {
        setTreeLoading(false);
      });
  };

  const handleSubmit = async (isSubmit: boolean) => {
    setLoading(isSubmit ? "SUBMIT" : "ADD");
    try {
      const values = await form.validateFields();

      // 验证报销项目
      const validItems = values.items?.filter(
        (item: AddReimburseRequestItem) => item.categoryId && item.amount > 0
      );
      if (!validItems || validItems.length === 0) {
        notification.error({ placement: 'top',
          message: t("common.tip"),
          description: t("reimburse.plSelectProducts"),
        });
        setLoading(undefined);
        return;
      }

      // 验证描述必填
      const emptyDescItem = validItems.find((item: AddReimburseRequestItem) => !item.description);
      if (emptyDescItem) {
        notification.error({ placement: 'top',
          message: t("common.tip"),
          description: t("components.plDictDesc"),
        });
        setLoading(undefined);
        return;
      }

      // 计算总金额
      const totalAmount = validItems.reduce((sum: number, item: AddReimburseRequestItem) => sum + (item.amount || 0), 0);

      // 转换为 AddReimburseRequestItem 格式
      const requestItems: AddReimburseRequestItem[] = validItems.map((item: AddReimburseRequestItem) => ({
        ...item,
        expenseDate: dayjs(item.expenseDate).startOf('day').valueOf(),
      }));

      const params: AddReimburseRequest = {
        title: values.title,
        content: values.content || "",
        totalAmount,
        remark: values.remark || "",
        items: requestItems,
        currency: exchangeRate.find(ext => ext.desc === "mainCurrency")?.key
      };

      let result = await addReimburse(params);
      if (isSubmit) result = await applicationSubmit(false, result.id);
      notification.success({ placement: 'top',
        message: isSubmit ? t("reimburse.submitSuccess") : t("common.addSuccess"),
      });
      onSuccess(result);
    } catch (error: unknown) {
      console.error("Form submit error:", error);
      notification.error({ placement: 'top',
        message: t("common.operationFailed"),
        description: t("common.pleaseTryAgain"),
      });
    } finally {
      setLoading(undefined);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleSetItemTab = (itemIndex: number, tabKey: string) => {
    setItemActiveTabs(prev => ({ ...prev, [itemIndex]: tabKey }));
  };

  const handleSetItemDocId = (itemIndex: number, docId: string) => {
    const items = form.getFieldValue("items") || [];
    items[itemIndex] = { ...items[itemIndex], docId };
    form.setFieldValue("items", items);
  };

  return (
    <Modal
      title={`${t("reimburse.addApplication")} ${t("reimburse.reimbursement")}`}
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      destroyOnClose
      width={1000}
      footer={
        <>
          <Button onClick={handleCancel}>{t("common.cancel")}</Button>
          <Button disabled={loading && loading !== "ADD"} type="primary" onClick={() => handleSubmit(false)} loading={loading === "ADD"}>
            {t("common.save")}
          </Button>
          <Button disabled={loading && loading !== "SUBMIT"} type="link" onClick={() => handleSubmit(true)} loading={loading === "SUBMIT"}>
            {t("reimburse.submit")}
          </Button>
        </>
      }
    >
      {treeLoading ? (
        <Spin style={{ marginTop: 16, width: "100%", height: 240 }} />
      ) : (
        <Form form={form} labelCol={{ style: { width: 100, textAlign: 'right' } }}>
          <Form.Item
            label={t("reimburse.title")}
            name="title"
            rules={[{ required: true, message: t("reimburse.plTitle") }]}
            style={{ marginBottom: 8 }}
          >
            <Input placeholder={t("reimburse.plTitle")} maxLength={200} />
          </Form.Item>
          <Form.Item
            label={t("reimburse.content")}
            name="content"
            rules={[{ required: true, message: t("reimburse.plContent") }]}
            style={{ marginBottom: 8 }}
          >
            <Input.TextArea
              placeholder={t("reimburse.plContent")}
              autoSize={{ minRows: 2, maxRows: 4 }}
              maxLength={500}
            />
          </Form.Item>
          <Form.Item label={t("reimburse.remark")} name="remark" style={{ marginBottom: 24 }}>
            <Input.TextArea
              placeholder={t("reimburse.plRemark")}
              autoSize={{ minRows: 2, maxRows: 4 }}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.List name="items" >
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    style={{
                      border: "1px solid #b0aeae",
                      borderRadius: 6,
                      padding: "0px 16px 24px",
                      marginBottom: 8,
                    }}
                  >
                    <Tabs
                      tabBarExtraContent={
                        fields.length > 1 && (
                          <SConModal title={t("common.rmTip")} content={t("common.rmContent")} onOk={() => Promise.resolve(remove(name))}>
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                            />
                          </SConModal>
                        )
                      }
                      activeKey={itemActiveTabs[name] || "info"}
                      onChange={(key) => handleSetItemTab(name, key)}
                      items={[
                        {
                          key: "info",
                          label: t("common.basicInfo"),
                          children: (
                            <>
                              <Flex align="center" gap={8} style={{ marginBottom: 8 }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "categoryId"]}
                                  label={t("layout.reimburseCategory")}
                                  rules={[{ required: true, message: t("layout.reimburseCategory") }]}
                                  style={{ marginBottom: 0, flex: 1 }}
                                >
                                  <ReimburseTreeSelect treeData={preparedTree} placeholder={t("layout.reimburseCategory")} />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, "amount"]}
                                  label={t("reimburse.amount")}
                                  rules={[{ required: true, message: t("reimburse.plAmount") }]}
                                  style={{ marginBottom: 0, flex: 1 }}
                                >
                                  <InputNumber changeOnWheel
                                    min={0}
                                    precision={2}
                                    style={{ width: "100%" }}
                                    placeholder={t("reimburse.amount")}
                                    formatter={(value: string | number | undefined) => value ? `${Number(value).toFixed(2)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                                    parser={(value: string | undefined) => value ? parseFloat(value.replace(/,/g, '')) : 0}
                                  />
                                </Form.Item>
                              </Flex>
                              <Flex gap={16} style={{ marginBottom: 8 }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "invoiceCount"]}
                                  label={t("reimburse.invoiceCount")}
                                  style={{ marginBottom: 0, flex: 1 }}
                                >
                                  <InputNumber changeOnWheel
                                    min={0}
                                    precision={0}
                                    style={{ width: "100%" }}
                                    placeholder={t("reimburse.invoiceCount")}
                                  />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, "expenseDate"]}
                                  label={t("reimburse.expenseDate")}
                                  rules={[{ required: true, message: t("reimburse.expenseDate") }]}
                                  style={{ marginBottom: 0, flex: 1 }}
                                >
                                  <DatePicker style={{ width: "100%" }} />
                                </Form.Item>
                              </Flex>
                              <Flex gap={16} style={{ marginBottom: 0 }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "description"]}
                                  label={t("common.desc")}
                                  rules={[{ required: true, message: t("components.plDictDesc") }]}
                                  style={{ marginBottom: 0, flex: 1 }}
                                >
                                  <Input.TextArea autoSize={{ minRows: 2, maxRows: 2 }} showCount placeholder={t("common.desc")} maxLength={500} />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, "remark"]}
                                  label={t("reimburse.remark")}
                                  style={{ marginBottom: 0, flex: 1 }}
                                >
                                  <Input.TextArea autoSize={{ minRows: 2, maxRows: 2 }} showCount placeholder={t("reimburse.remark")} maxLength={500} />
                                </Form.Item>
                              </Flex>
                            </>
                          ),
                        },
                        {
                          key: "attachment",
                          label: t("common.attachment"),
                          children: (
                            <SDoc
                              docId={form.getFieldValue(["items", name, "docId"]) || ""}
                              onChangeDocId={(docId) => handleSetItemDocId(name, docId)}
                              handleNewDocId={(docId) => handleSetItemDocId(name, docId)}
                            />
                          ),
                        },
                      ]}
                    />
                  </div>
                ))}
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="dashed"
                    onClick={() => {
                      const newIndex = (form.getFieldValue("items") || []).length;
                      setItemActiveTabs(prev => ({ ...prev, [newIndex]: "info" }));
                      add({ expenseDate: dayjs() });
                    }}
                    block
                    icon={<PlusOutlined />}
                  >
                    {t("common.add")}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      )}
    </Modal>
  );
};

export default AddReimbursement;
